function sleep(ms) { 
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Export data to specified format
 * @param app - Qlik app instance
 * @param id - Qlik object ID (for charts or containers)
 * @param format - OOXML, CSV_C, CSV_T
 * @param state - "A" (all values), "P" (possible values)
 * @param onExportStart - callback when export begins
 * @param onExportFinish - callback when export ends (link, error)
 */
export async function ExportData({
  app,
  id,
  format = "OOXML",
  state = "P",
  onExportStart = null,
  onExportFinish = null
}) {
  const exportType = "data";
  let vis = await app.visualization.get(id);

  if (vis.model?.layout?.qInfo) {
    const { layout } = vis.model;
    const qType = layout.qInfo.qType;

    if (qType === 'container') {
      // For container, pick activeTab or defaultTab
      const selectTab = layout.activeTab || layout.defaultTab;
      vis = await app.visualization.get(selectTab);

    } else if (qType === 'sn-tabbed-container') {
      // For tabbed container, map cached/default tab to real object
      const selectTab = layout.cachedTabId || layout.defaultTabId;
      const childItems = layout.qChildList?.qItems || [];
      const child = childItems.find(item =>
        item.qData?.childRefId === selectTab
      );
      const realId = child ? child.qInfo.qId : selectTab;
      vis = await app.visualization.get(realId);
    }
    // Note: sn-layout-container handled by React component, not here
  }

  // Invoke start callback
  if (onExportStart) {
    await onExportStart({ vis, exportType });
  }

  // Perform export
  vis.exportData({ format, state })
    .then(async (link) => {
      try {
        if (onExportFinish) {
          await onExportFinish({ vis, link, error: null, exportType });
        } else {
          window.open(link, "_blank");
        }
      } finally {
        vis.close();
      }
    })
    .catch((error) => {
      vis.close();
      if (onExportFinish) {
        onExportFinish({ vis: null, link: null, error, exportType });
      }
    });
}


/**
 * Export image
 * @param app - qlik app
 * @param id - qlik object id
 * @param width - width in pixels
 * @param height - height in pixels
 * @param format - png, jpg
 */
export function ExportImg({ app, id, width = 1920, height = 1080, format = "jpg",
  onExportStart = null, onExportFinish=null }) {
  const exportType = "image";
  app.visualization.get(id).then(async function (vis) {
    const settings = { format, height, width };
    if(onExportStart) 
      await onExportStart({ vis, settings, exportType });

    vis.exportImg(settings).then(async function (link) {
      // console.log(link);
      // window.location.replace(link);
      try {
        if(onExportFinish)
          await onExportFinish({ vis, link, error: null, exportType });
        else
          window.open(link, "_blank");
      } finally {
        vis.close();
      }
    }).catch(function (error) {
      onExportFinish && onExportFinish({ vis: null, link: null, error, exportType });
      vis && vis.close();
    });
    // onExportStart && onExportStart();
  });
}

/**
 * Export PDF
 * @param app - qlik app
 * @param id - qlik object id
 * @param documentSize - Page size of the output PDF: a3, a4, a5, a6, a7
 * @param orientation - portrait, landscape
 * @param aspectRatio - 0, 2
 */
export function ExportPdf({ app, id, documentSize = 'a4',
  orientation = 'landscape', aspectRatio = 2,
  onExportStart = null, onExportFinish=null }) {
  const exportType = "pdf";
  app.visualization.get(id).then(async function (vis) {
    const settings = { documentSize, orientation, aspectRatio };
    if(onExportStart) 
      await onExportStart({ vis, settings, exportType });

    vis.exportPdf(settings).then(async function (link) {
      // console.log(link);
      // window.location.replace(link);      
      try {
        if(onExportFinish)
          await onExportFinish({ vis, link, error: null, exportType });
        else
          window.open(link, "_blank");
      } finally {
        vis.close();
      }
    }).catch(function (error) {
      vis && vis.close();
      onExportFinish && onExportFinish({ vis: null, link: null, error, exportType });
    });
    // onExportStart && onExportStart();
  });
}

export function ApplyBookmark(app, id) {
  return app.bookmark.apply(id);
}

// To fix issue when bookmark created it can be published after some time
let justCreated = {};
const bmTimeoutMs = process.env.REACT_APP_AUTOSAVE_TIMEOUT_MS || 30000;
function setJustCreated(bid) {
  justCreated[bid] = setTimeout(() => clearJustCreated(bid), bmTimeoutMs);
}

function clearJustCreated(bid) {  
  delete justCreated[bid];
}

function isJustCreated(bid) {
  return justCreated[bid];
}

export async function CreateBookmark(app, title, description, sheetId) {
  //const b = await app.bookmark.create(title, description, sheetId);
  const b = await app.model.enigmaModel.createBookmark(
    {
      qInfo: {
        qType: "bookmark"
      },
      qMetaDef: {
        title,
        description,
        isExtended: true,
        // approved: false,
        // published: false,
      },
      qIncludeVariables: true,
      page: sheetId ? sheetId : '/',
      // "qDistinctValues": false
    },
    []
  );

/*   const props = await b.getProperties();
  if(sheetId)
    props.page = sheetId; //`${window.location.pathname}${window.location.hash}`;

  if(props.qMetaDef && !props.qMetaDef.createdDate) {
    props.qMetaDef.createdDate = props.creationDate;
  }
  // props.qInfo.qType = `bookmark/${userId}`;
  await b.setProperties(props);
  setJustCreated(props.qInfo.qId); */
  await b.publish(); // to force engine call savePropertiesCaches to store data in db using qrs
  await b.unPublish();
  return b;
}

export function GetBookmark(app, id) {
  return app.model.enigmaModel.getBookmark(id);
}

export function RemoveBookmark(app, id) {
  return app.bookmark.remove(id);
}

function GetFullUserId(user) {
  return user ? `${user.UserDirectory}/${user.getFullUserId()}` : '';
}

export async function PublishBookmark(app, id, user) {
  // reload !
  // const books = await GetBookmarks(app, user);
  // console.log(books);
  if(isJustCreated(id))
    await sleep(bmTimeoutMs);

  let b = await app.model.enigmaModel.getBookmark(id);

  const userId = GetFullUserId(user);
  const props = await b.getProperties();
  props.qInfo.qType = `bookmark/${userId}`; // Makes published bookmark invisible to other users
  await b.setProperties(props);
  await b.publish();
}

export async function UnpublishBookmark(app, id) {
  const b = await app.model.enigmaModel.getBookmark(id);
  await b.unPublish();

  const props = await b.getProperties();
  props.qInfo.qType = 'bookmark';
  await b.setProperties(props);  
}

export async function GetBookmarks(app, user) {
    const userId = GetFullUserId(user);
    app.model.enigmaModel.session.config.protocol.delta = false;
    const bookmarks = await app.model.enigmaModel.getBookmarks({
     qOptions: {
       qTypes: [
         'bookmark', `bookmark/${userId}` // bookmark's types to load
       ],
       qData: {
        creationDate: "/creationDate",
        //  qBookmark: "", // do not load - just remove to load info
       },
      //  qData: {
      //    title: "/qMetaDef/title",
      //    creationDate: "/creationDate",
      //    description: "/qMetaDef/description",
      //    // sheetId: "/sheetId"
      //    ///selectionFields
      //  },
       qIncludePatches: false,
     }});

     // const l = await app.getList("BookmarkList");
     // return l.layout.qBookmarkList.qItems);
     app.model.enigmaModel.session.config.protocol.delta = true;

    //  return bookmarks;
    return bookmarks.filter(b => {
      return b.qMeta.approved 
      || (b.qMeta.owner.userDirectory === user.UserDirectory 
        && b.qMeta.owner.userId === user.getFullUserId())
      });
}